export class IframeController {
	constructor(previewIframe, rulesPreviewIframe) {
		this.previewIframe = previewIframe;
		this.rulesPreviewIframe = rulesPreviewIframe;
		this.previewIframeLoadHandler = null;
		this.rulesViewIframeLoadHandler = null;
		this.headerPostIdCache = new Map();
		this.headerPostIdRequests = new Map();
		this.previewIframeNavigationData = this.createEmptyNavigationData();
		this.rulesViewIframeNavigationData = this.createEmptyNavigationData();
	}

	createEmptyNavigationData() {
		return {
			url: '',
			postId: null
		};
	}

	normalizePostId(value) {
		const parsedPostId = Number.parseInt(String(value), 10);

		if (!Number.isInteger(parsedPostId) || parsedPostId <= 0) {
			return null;
		}

		return parsedPostId;
	}

	normalizePageType(value) {
		const normalizedValue = String(value || '').trim().toLowerCase();

		if (normalizedValue === 'home' || normalizedValue === 'archive' || normalizedValue === 'singular') {
			return normalizedValue;
		}

		return '';
	}

	normalizeIframeUrl(url) {
		const rawUrl = String(url || '').trim();

		if (!rawUrl) {
			return '';
		}

		if (rawUrl === 'about:blank') {
			return rawUrl;
		}

		try {
			return new URL(rawUrl, window.location.href).href;
		} catch {
			return rawUrl;
		}
	}

	getStoredNavigationData(iframeNode) {
		if (iframeNode === this.rulesPreviewIframe) {
			return this.rulesViewIframeNavigationData;
		}

		return this.previewIframeNavigationData;
	}

	setStoredNavigationData(iframeNode, navigationData) {
		const normalizedNavigationData = {
			url: this.normalizeIframeUrl(navigationData?.url || ''),
			postId: this.normalizePostId(navigationData?.postId)
		};

		if (iframeNode === this.rulesPreviewIframe) {
			this.rulesViewIframeNavigationData = normalizedNavigationData;
			return;
		}

		this.previewIframeNavigationData = normalizedNavigationData;
	}

	getIframeRuntimeNavigationData(iframeNode) {
		if (!iframeNode) {
			return this.createEmptyNavigationData();
		}

		let iframeUrl = this.normalizeIframeUrl(iframeNode.src || '');
		let postId = null;

		try {
			const iframeWindow = iframeNode.contentWindow;

			if (iframeWindow?.location?.href) {
				iframeUrl = this.normalizeIframeUrl(iframeWindow.location.href);
			}

			postId = this.normalizePostId(iframeWindow?.a11yfixClientData?.postId);
		} catch {
			postId = null;
		}

		const storedNavigationData = this.getStoredNavigationData(iframeNode);

		if (storedNavigationData?.url && storedNavigationData.url === iframeUrl && storedNavigationData.postId) {
			postId = storedNavigationData.postId;
		}

		return {
			url: iframeUrl,
			postId
		};
	}

	async resolveHeaderPostId(url) {
		const normalizedUrl = this.normalizeIframeUrl(url);

		if (!normalizedUrl || normalizedUrl === 'about:blank') {
			return null;
		}

		let parsedUrl;

		try {
			parsedUrl = new URL(normalizedUrl, window.location.href);
		} catch {
			return null;
		}

		if (parsedUrl.origin !== window.location.origin) {
			return null;
		}

		if (this.headerPostIdCache.has(parsedUrl.href)) {
			return this.headerPostIdCache.get(parsedUrl.href);
		}

		if (this.headerPostIdRequests.has(parsedUrl.href)) {
			return this.headerPostIdRequests.get(parsedUrl.href);
		}

		const requestPromise = fetch(parsedUrl.href, {
			method: 'GET',
			credentials: 'same-origin',
			redirect: 'follow'
		})
			.then((response) => {
				if (!response.ok) {
					return null;
				}

				const pageType = this.normalizePageType(response.headers.get('X-A11yFix-Page-Type'));

				if (pageType === 'home' || pageType === 'archive') {
					return null;
				}

				return this.normalizePostId(response.headers.get('X-A11yFix-Post-Id'));
			})
			.catch(() => null)
			.then((postId) => {
				this.headerPostIdCache.set(parsedUrl.href, postId);
				this.headerPostIdRequests.delete(parsedUrl.href);
				return postId;
			});

		this.headerPostIdRequests.set(parsedUrl.href, requestPromise);

		return requestPromise;
	}

	async getResolvedNavigationData(iframeNode) {
		const runtimeNavigationData = this.getIframeRuntimeNavigationData(iframeNode);
		const headerPostId = await this.resolveHeaderPostId(runtimeNavigationData.url);
		const navigationData = {
			url: runtimeNavigationData.url,
			postId: headerPostId ?? runtimeNavigationData.postId
		};

		this.setStoredNavigationData(iframeNode, navigationData);

		return navigationData;
	}

	isIframeReadyForResize(iframeNode) {
		if (!iframeNode) {
			return false;
		}

		const sourceUrl = String(iframeNode.getAttribute('src') || iframeNode.src || '').trim();

		if (!sourceUrl || sourceUrl === 'about:blank') {
			return false;
		}

		try {
			const parsed = new URL(sourceUrl, window.location.href);
			return parsed.href !== 'about:blank';
		} catch {
			return true;
		}
	}

	clearPreviewIframe() {
		if (!this.previewIframe) {
			return;
		}

		this.previewIframeNavigationData = this.createEmptyNavigationData();
		this.previewIframe.src = 'about:blank';
	}

	clearRulesViewIframe() {
		if (!this.rulesPreviewIframe) {
			return;
		}

		this.rulesViewIframeNavigationData = this.createEmptyNavigationData();
		this.rulesPreviewIframe.src = 'about:blank';
	}

	loadIframeUrl(url) {
		this.syncPreviewIframeHeight();
		return this.loadUrlInIframe(this.previewIframe, url);
	}

	loadRulesViewUrl(url) {
		this.syncRulesViewIframeHeight();
		return this.loadUrlInIframe(this.rulesPreviewIframe, url);
	}

	loadUrlInIframe(iframeNode, url) {
		if (!iframeNode) {
			return Promise.resolve({
				loaded: false,
				documentNode: null,
				postId: null
			});
		}

		const requestedUrl = this.normalizeIframeUrl(url);
		const headerPostIdPromise = this.resolveHeaderPostId(requestedUrl);

		return new Promise((resolve) => {
			let isFinished = false;

			const finish = (result) => {
				if (isFinished) {
					return;
				}

				isFinished = true;
				clearTimeout(timeoutId);
				window.clearTimeout(loadFallbackTimeoutId);
				window.removeEventListener('message', onMessage);
				iframeNode.removeEventListener('load', onLoad);
				iframeNode.removeEventListener('error', onError);

				void headerPostIdPromise.then((headerPostId) => {
					const resolvedUrl = this.normalizeIframeUrl(
						result?.documentNode?.location?.href || requestedUrl || iframeNode.src || ''
					);
					const resolvedPostId = headerPostId ?? this.normalizePostId(result?.postId);
					const resolvedResult = {
						...result,
						url: resolvedUrl,
						postId: resolvedPostId
					};

					this.setStoredNavigationData(iframeNode, resolvedResult);
					resolve(resolvedResult);
				});
			};

			const onMessage = (event) => {
				if (event.origin !== window.location.origin) {
					return;
				}

				if (event.source !== iframeNode.contentWindow) {
					return;
				}

				if (event.data?.type !== 'a11yfix_iframe_document_ready') {
					return;
				}

				const parsedPostId = Number.parseInt(String(event.data?.postId), 10);
				const postId = Number.isInteger(parsedPostId) && parsedPostId > 0 ? parsedPostId : null;

				finish({
					loaded: true,
					documentNode: iframeNode.contentDocument || null,
					postId
				});
			};

			const onLoad = () => {
				this.syncPreviewIframeHeight();
				this.syncRulesViewIframeHeight();
				window.setTimeout(() => {
					this.syncPreviewIframeHeight();
					this.syncRulesViewIframeHeight();
				}, 50);

				loadFallbackTimeoutId = window.setTimeout(() => {
					finish({
						loaded: true,
						documentNode: iframeNode.contentDocument || null,
						postId: null
					});
				}, 1000);
			};

			const onError = () => {
				finish({
					loaded: false,
					documentNode: null,
					postId: null
				});
			};

			const timeoutId = window.setTimeout(() => {
				finish({
					loaded: false,
					documentNode: null,
					postId: null
				});
			}, 20000);

			let loadFallbackTimeoutId = 0;

			window.addEventListener('message', onMessage);
			iframeNode.addEventListener('load', onLoad);
			iframeNode.addEventListener('error', onError);
			iframeNode.src = url;
		});
	}

	syncRulesViewIframeHeight() {
		this.syncPanelIframeHeight('a11yfix-panel-rules-view', this.rulesPreviewIframe);
	}

	syncPreviewIframeHeight() {
		this.syncPanelIframeHeight('a11yfix-panel-iframe', this.previewIframe);
	}

	syncPanelIframeHeight(panelId, iframeNode) {
		const panelNode = document.getElementById(panelId);
		const iframeWrapper = panelNode?.querySelector('.a11yfix-iframe-wrapper');
		const controlsNode = panelNode?.querySelector('.a11yfix-controls');

		if (!panelNode || !iframeWrapper || !iframeNode) {
			return;
		}

		if (!this.isIframeReadyForResize(iframeNode)) {
			return;
		}

		if (!panelNode.classList.contains('is-active')) {
			return;
		}

		const panelHeight = panelNode.clientHeight;
		const controlsHeight = controlsNode ? controlsNode.offsetHeight : 0;
		const panelStyle = window.getComputedStyle(panelNode);
		const marginBottom = Number.parseFloat(panelStyle.marginBottom || '0') || 0;
		const availableHeight = Math.floor(panelHeight - controlsHeight - marginBottom);
		const minHeight = 220;
		const viewportOffset = 120;
		const maxHeight = Math.max(minHeight, window.innerHeight - viewportOffset);
		const clampedHeight = Math.max(minHeight, Math.min(maxHeight, availableHeight));

		if (!Number.isFinite(clampedHeight)) {
			return;
		}

		const iframeHeight = `${clampedHeight}px`;
		iframeWrapper.style.height = iframeHeight;
		iframeNode.style.height = iframeHeight;
	}

	bindRulesViewIframeNavigation(onNavigate) {
		if (!this.rulesPreviewIframe || typeof onNavigate !== 'function') {
			return;
		}

		if (this.rulesViewIframeLoadHandler) {
			this.rulesPreviewIframe.removeEventListener('load', this.rulesViewIframeLoadHandler);
		}

		this.rulesViewIframeLoadHandler = async () => {
			onNavigate(await this.getRulesViewIframeNavigationDataAsync());
		};

		this.rulesPreviewIframe.addEventListener('load', this.rulesViewIframeLoadHandler);
	}

	bindPreviewIframeNavigation(onNavigate) {
		if (!this.previewIframe || typeof onNavigate !== 'function') {
			return;
		}

		if (this.previewIframeLoadHandler) {
			this.previewIframe.removeEventListener('load', this.previewIframeLoadHandler);
		}

		this.previewIframeLoadHandler = async () => {
			onNavigate(await this.getPreviewIframeNavigationDataAsync());
		};

		this.previewIframe.addEventListener('load', this.previewIframeLoadHandler);
	}

	getPreviewIframeNavigationData() {
		return this.getIframeRuntimeNavigationData(this.previewIframe);
	}

	async getPreviewIframeNavigationDataAsync() {
		return this.getResolvedNavigationData(this.previewIframe);
	}

	getRulesViewIframeNavigationData() {
		return this.getIframeRuntimeNavigationData(this.rulesPreviewIframe);
	}

	async getRulesViewIframeNavigationDataAsync() {
		return this.getResolvedNavigationData(this.rulesPreviewIframe);
	}
}
